import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { ExpandMore, ExpandLess, TrendingUp, TrendingDown } from '@mui/icons-material';
import { 
  formatNumber, 
  formatConversionRate, 
  getPerformanceLevel,
  getStageName 
} from '../../../../lib/utils/conversionFunnelUtils';
import { brandColors } from '../../../../theme/theme';

/**
 * FunnelStageCard Component
 * 
 * Displays a single funnel stage with:
 * - Count and percentages
 * - Conversion rate from previous stage
 * - Performance indicator
 * - Expandable demographic breakdown
 */
const FunnelStageCard = ({ 
  stageNumber, 
  stageData, 
  demographicBreakdown = null,
  showDemographics = true,
  totalResponses = null // Total responses for calculating pct_of_responses if missing
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!stageData) {
    return null;
  }

  const stageName = getStageName(stageNumber);
  const performance = stageData.conversion_rate 
    ? getPerformanceLevel(stageData.conversion_rate)
    : null;

  // Calculate pct_of_responses if missing or 0
  const pctOfResponses = stageData.pct_of_responses !== undefined && stageData.pct_of_responses > 0
    ? stageData.pct_of_responses
    : (totalResponses && stageData.count && totalResponses > 0)
      ? (stageData.count / totalResponses) * 100
      : 0;

  // Get demographic segments if available
  const segments = demographicBreakdown && demographicBreakdown.stages 
    ? demographicBreakdown.stages.find(s => s.stage === stageNumber)?.segments || {}
    : {};

  const hasSegments = Object.keys(segments).length > 0;

  return (
    <Card 
      sx={{ 
        height: '100%',
        border: performance ? `2px solid ${performance.color}40` : '1px solid #e0e0e0',
        backgroundColor: performance ? `${performance.color}05` : 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5
            }}
          >
            Stage {stageNumber}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brandColors.primary }}>
            {stageName}
          </Typography>
        </Box>

        {/* Main Metric */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
            {formatNumber(stageData.count)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatConversionRate(pctOfResponses, 1)} of total responses
          </Typography>
          {stageData.pct_of_aware !== undefined && stageNumber > 1 && (
            <Typography variant="body2" color="text.secondary">
              {formatConversionRate(stageData.pct_of_aware, 1)} of aware
            </Typography>
          )}
        </Box>

        {/* Conversion Rate */}
        {stageData.conversion_rate !== undefined && performance && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {stageData.conversion_rate >= 50 ? (
                <TrendingUp sx={{ color: performance.color, fontSize: 20 }} />
              ) : (
                <TrendingDown sx={{ color: performance.color, fontSize: 20 }} />
              )}
              <Typography variant="h5" sx={{ fontWeight: 700, color: performance.color }}>
                {formatConversionRate(stageData.conversion_rate)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Conversion from previous stage
            </Typography>
            <Chip
              label={performance.label}
              size="small"
              sx={{
                backgroundColor: `${performance.color}20`,
                color: performance.color,
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>
        )}

        {/* Demographic Breakdown Toggle */}
        {showDemographics && hasSegments && (
          <Box>
            <Box
              onClick={() => setExpanded(!expanded)}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                View Demographic Breakdown
              </Typography>
              <IconButton size="small">
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={expanded}>
              <Box sx={{ mt: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          Segment
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          Count
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          % of Stage
                        </TableCell>
                        {stageNumber > 1 && (
                          <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                            Conversion
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(segments)
                        .sort(([, a], [, b]) => b.count - a.count)
                        .map(([segment, metrics]) => (
                          <TableRow key={segment} hover>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              {segment}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {formatNumber(metrics.count)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {formatConversionRate(metrics.pct_of_stage)}
                            </TableCell>
                            {stageNumber > 1 && (
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                {metrics.conversion_from_previous_stage !== undefined
                                  ? formatConversionRate(metrics.conversion_from_previous_stage)
                                  : '—'}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FunnelStageCard;

